import {
  useMutation,
  useQueryClient,
  UseMutationResult
} from '@tanstack/react-query'
import {
  updateBackofficeVisibility,
  updateSettingStatus,
  updateSettingSubtext
} from '../../apis/settingsDefinitions'
import { queryKeys } from '../../lib/queryKeys'
import type { FeatureFlagStatus } from '../../constants/featureFlagStatus'

interface UpdateBackofficeVisibilityVariables {
  definitionId: number
  isBackofficeVisible: boolean
}

interface UpdateSubtextVariables {
  definitionId: number
  subtext: string
}

interface UpdateStatusVariables {
  definitionId: number
  status: FeatureFlagStatus
}

// Mutation: Update backoffice visibility (GLOBAL setting)
export const useUpdateBackofficeVisibility = (): UseMutationResult<
  any,
  Error,
  UpdateBackofficeVisibilityVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      definitionId,
      isBackofficeVisible
    }: UpdateBackofficeVisibilityVariables) =>
      updateBackofficeVisibility(definitionId, isBackofficeVisible),
    onSuccess: () => {
      // Invalidate all organization queries since this is a global change
      // that affects the setting_definition table (not org-specific)
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all
      })
    }
  })
}

// Mutation: Advance the feature flag lifecycle status (GLOBAL setting)
export const useUpdateSettingStatus = (): UseMutationResult<
  any,
  Error,
  UpdateStatusVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ definitionId, status }: UpdateStatusVariables) =>
      updateSettingStatus(definitionId, status),
    onSuccess: () => {
      // Global change to setting_definition: the flag catalog shows the status,
      // and so does the feature flags section of every org's settings.
      queryClient.invalidateQueries({
        queryKey: queryKeys.featureFlags.all
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all
      })
    }
  })
}

// Mutation: Update subtext/description (GLOBAL setting)
export const useUpdateSettingSubtext = (): UseMutationResult<
  any,
  Error,
  UpdateSubtextVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ definitionId, subtext }: UpdateSubtextVariables) =>
      updateSettingSubtext(definitionId, subtext),
    onSuccess: () => {
      // Global change to the setting_definition table — refresh org settings
      // so the new description shows everywhere. The FF combobox/filter fetch
      // live on each search, so they pick up the change without invalidation.
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.all
      })
    }
  })
}
